/** Keep in sync with apps/bot/utils/donorAnnounce.js */
export const DEFAULT_DONOR_ANNOUNCE =
  "☾ **Nocturne supporter** · {user} just became **{tier}**.\nThank you for backing the server, Kami, and helping keep the quiet lit.";

const LEGACY_DONOR_ANNOUNCES = new Set([
  "☾ {user} is now a **{tier}** of Nocturne.\nThank you for keeping the quiet lit.",
  "🌙 **Site support** · {user} just became **{tier}** on kamigrove.com.\nThank you for backing Nocturne and helping keep the quiet lit.",
]);

export function resolveDonorAnnounceMessage(stored) {
  const text = String(stored || "").trim();
  if (!text || LEGACY_DONOR_ANNOUNCES.has(text)) {
    return DEFAULT_DONOR_ANNOUNCE;
  }
  return text;
}

/** Season pass. Keep aligned with apps/bot/utils/nocturnePass.js */
export const FALLBACK_NOCTURNE_PASS = {
  id: "nocturne_pass",
  name: "Nocturne Pass · Season 1",
  kind: "one_time",
  amountCents: 299,
  fullAmountCents: 299,
  supporterAmountCents: 224,
  currency: "usd",
  badgeEmoji: "🌑",
  blurb:
    "Unlock the premium reward track for this season — Essence, XP, items, exclusive themes, and badges.",
  perks: [
    "Premium track for the current season only",
    "Exclusive season theme & badge on the premium ladder",
    "Active Patron/Benefactor: included while subscribed",
    "Lifetime Supporter: 25% off ($2.24)",
    "Free track stays available to everyone",
  ],
  scope: "personal",
  discounted: false,
  owned: false,
  included: false,
};

/** One-time per-server unlock. Keep aligned with apps/bot/utils/guildHost.js */
export const FALLBACK_SERVER_HOST = {
  id: "server_host",
  name: "Server Host",
  kind: "one_time",
  amountCents: 800,
  currency: "usd",
  badgeEmoji: "🏰",
  blurb:
    "Optional one-time add-on for a single Discord server — branding, templates, multi-desk tickets, stats, and other conveniences.",
  perks: [
    "Kami per-server nickname, bio, avatar, and banner",
    "Embed template library (/host template)",
    "Multi-desk tickets with optional intake forms",
    "Ticket auto-close + staff digest",
    "Scheduled embed posts",
    "Brand kit apply + purge presets",
    "Server insights (/host stats)",
    "Longer TTS (500 chars) · extra layout backups (25)",
    "Partner & stream polish extras · priority music queue",
    "Attaches to one server forever (not a subscription)",
  ],
  scope: "guild",
};

/**
 * Fallback tier cards for the Support/Donate page when the API is slow/empty.
 * Keep perk bullets aligned with apps/bot/utils/donorTiers.js.
 */
export const FALLBACK_DONOR_TIERS = [
  {
    id: "supporter",
    name: "Supporter",
    kind: "one_time",
    amountCents: 200,
    currency: "usd",
    badgeEmoji: "🌙",
    blurb:
      "A small one-time thank-you: cosmetics, shop discount, Essence, XP, and a Lunar Surge charm.",
    perks: [
      "Kami Supporter badge",
      "Title: Supporter of the Quiet",
      "Exclusive theme: Quiet Ember",
      "3% shop discount",
      "Scaled Essence gift + ~35% of a level in XP",
      "Lunar Surge charm on purchase",
      "/patron cooldowns · fortune · palette · toast · echo",
      "90-day & DM reminders · confession colors",
      "Daily streak grace — restore if you miss by ≤1 day (2+ days is too late)",
      "`/pet care` — max remaining feed + play (and ability if ready) in one go",
      "`/customize color` · personal command embed colors",
      "Personal perks work in every server",
      "Keeps forever (even if you never subscribe)",
    ],
  },
  {
    id: "patron",
    name: "Patron",
    kind: "subscription",
    amountCents: 300,
    currency: "usd",
    interval: "month",
    badgeEmoji: "💫",
    blurb:
      "A light monthly pledge with XP boosts, charms each payment, and Moonlit Gift.",
    perks: [
      "Everything in Supporter (if not already owned)",
      "Nocturne Patron badge (while subscribed)",
      "Title: Moonlit Patron",
      "Exclusive theme: Moonlit Gift",
      "7% shop discount (while subscribed)",
      "+5% XP · shorter XP cooldown · +50 /daily Essence",
      "Each payment: Essence + XP + Lunar Surge & Gilded Lure",
      "Spirit egg on first sub · 35% renew egg chance / month",
      "/patron digest · glow · tarot · constellation · vignette · spotlight · remix",
      "Recurring daily/weekly /remind",
      "2 garden plots · 25% faster growth · free water / day",
      "Automatic giveaway +1 entry (if server allows)",
      "Profile frame · longer AFK + [AFK] nickname",
      "Nocturne Pass premium included while subscribed",
    ],
  },
  {
    id: "benefactor",
    name: "Benefactor",
    kind: "subscription",
    amountCents: 600,
    currency: "usd",
    interval: "month",
    badgeEmoji: "👑",
    blurb:
      "The fullest monthly pledge: strongest XP/daily buffs, renew charms, and a free custom title.",
    perks: [
      "Everything in Patron",
      "Nocturne Benefactor badge (while subscribed)",
      "Title: Benefactor of Nocturne",
      "Exclusive theme: Benefactor Crest",
      "10% shop discount (while subscribed)",
      "+10% XP · faster cooldown · +120 /daily Essence",
      "Each payment: Essence + larger XP + Eclipse Ascension & Gilded Lure",
      "Free custom title unlock (lifetime)",
      "/patron seance · sigil · react",
      "Up to 2 staff-approved custom message triggers (if server allows)",
      "/patron announce (1/day, if server allows) · accent · note · monthly refresh",
      "Profile crest flair · early-access flag",
      "Nocturne Pass premium included while subscribed",
    ],
  },
];
